// Cómo se llaman en pantalla las cifras que este módulo sabe medir.
//
// Aparte del resto de los textos por un motivo que no es de tamaño: estas claves
// son un CONTRATO con el catálogo de analítica (`backend/analytics.js`), no copia
// de una pantalla. Cada `labelKey` que aquel fichero declara tiene aquí su
// traducción en los cuatro idiomas, y una que falte no rompe nada — sale el id
// hecho legible, en inglés, en medio de una pantalla en castellano.
//
// La ayuda de un dataset (`help`) es lo que alguien lee en el desplegable de
// Explorar antes de elegirlo. Dice qué es UNA FILA, que es la pregunta que de
// verdad distingue dos conjuntos de datos parecidos.

const analytics = {
  es: {
    assets: {
      label: 'Censo de activos',
      help: 'Una fila por activo: plantas, líneas, máquinas y componentes',
    },
    stops: {
      label: 'Paradas',
      help: 'Una fila por parada de máquina, programada o no, con sus horas',
    },
    failures: {
      label: 'Averías',
      help: 'Una fila por avería, con su causa y lo que interrumpió. No lleva MTBF: haría falta horas de marcha',
    },
    dates: {
      installed: 'Fecha de instalación',
      created: 'Fecha de alta',
      stopStarted: 'Inicio de la parada',
      stopEnded: 'Fin de la parada',
      failed: 'Fecha de la avería',
    },
    metrics: {
      assets: 'Activos',
      active: 'En servicio',
      outOfService: 'Fuera de servicio',
      critical: 'Críticos',
      age: 'Antigüedad media',
      downtime: 'Horas de parada',
      stops: 'Paradas',
      stillStopped: 'Paradas abiertas',
      averageStop: 'Parada media',
      failures: 'Averías',
      stopping: 'Averías que pararon',
      interruption: 'Horas perdidas',
      mttr: 'MTTR',
      stoppingRate: 'Peso de las bloqueantes',
    },
    dimensions: {
      level: 'Nivel',
      type: 'Tipo de máquina',
      status: 'Estado',
      site: 'Centro',
      criticality: 'Criticidad',
      manufacturer: 'Fabricante',
      responsible: 'Responsable',
      stopAsset: 'Máquina',
      stopKind: 'Programada o no',
      stopSource: 'Quién la registró',
      failureAsset: 'Máquina',
      failureType: 'Tipo de avería',
      cause: 'Causa',
      severity: 'Gravedad',
      detection: 'Cómo se detectó',
      failureSource: 'Quién la registró',
    },
    filters: {
      criticality: 'Criticidad',
      stopHours: 'Horas de la parada',
      interruption: 'Horas perdidas',
    },
  },
  ca: {
    assets: {
      label: 'Cens d\'actius',
      help: 'Una fila per actiu: plantes, línies, màquines i components',
    },
    stops: {
      label: 'Aturades',
      help: 'Una fila per aturada de màquina, programada o no, amb les seves hores',
    },
    failures: {
      label: 'Avaries',
      help: 'Una fila per avaria, amb la seva causa i el que va interrompre. No porta MTBF: caldrien hores de marxa',
    },
    dates: {
      installed: 'Data d\'instal·lació',
      created: 'Data d\'alta',
      stopStarted: 'Inici de l\'aturada',
      stopEnded: 'Fi de l\'aturada',
      failed: 'Data de l\'avaria',
    },
    metrics: {
      assets: 'Actius',
      active: 'En servei',
      outOfService: 'Fora de servei',
      critical: 'Crítics',
      age: 'Antiguitat mitjana',
      downtime: 'Hores d\'aturada',
      stops: 'Aturades',
      stillStopped: 'Aturades obertes',
      averageStop: 'Aturada mitjana',
      failures: 'Avaries',
      stopping: 'Avaries que van aturar',
      interruption: 'Hores perdudes',
      mttr: 'MTTR',
      stoppingRate: 'Pes de les bloquejants',
    },
    dimensions: {
      level: 'Nivell',
      type: 'Tipus de màquina',
      status: 'Estat',
      site: 'Centre',
      criticality: 'Criticitat',
      manufacturer: 'Fabricant',
      responsible: 'Responsable',
      stopAsset: 'Màquina',
      stopKind: 'Programada o no',
      stopSource: 'Qui la va registrar',
      failureAsset: 'Màquina',
      failureType: 'Tipus d\'avaria',
      cause: 'Causa',
      severity: 'Gravetat',
      detection: 'Com es va detectar',
      failureSource: 'Qui la va registrar',
    },
    filters: {
      criticality: 'Criticitat',
      stopHours: 'Hores de l\'aturada',
      interruption: 'Hores perdudes',
    },
  },
  en: {
    assets: {
      label: 'Asset census',
      help: 'One row per asset: plants, lines, machines and components',
    },
    stops: {
      label: 'Stops',
      help: 'One row per machine stop, planned or not, with its hours',
    },
    failures: {
      label: 'Failures',
      help: 'One row per failure, with its cause and what it interrupted. No MTBF: that needs running hours',
    },
    dates: {
      installed: 'Installed on',
      created: 'Added',
      stopStarted: 'Stop started',
      stopEnded: 'Stop ended',
      failed: 'Failure date',
    },
    metrics: {
      assets: 'Assets',
      active: 'In service',
      outOfService: 'Out of service',
      critical: 'Critical',
      age: 'Average age',
      downtime: 'Downtime',
      stops: 'Stops',
      stillStopped: 'Still stopped',
      averageStop: 'Average stop',
      failures: 'Failures',
      stopping: 'Failures that stopped it',
      interruption: 'Hours lost',
      mttr: 'MTTR',
      stoppingRate: 'Share that stopped it',
    },
    dimensions: {
      level: 'Level',
      type: 'Machine kind',
      status: 'Status',
      site: 'Site',
      criticality: 'Criticality',
      manufacturer: 'Manufacturer',
      responsible: 'Responsible',
      stopAsset: 'Machine',
      stopKind: 'Planned or not',
      stopSource: 'Recorded by',
      failureAsset: 'Machine',
      failureType: 'Failure type',
      cause: 'Cause',
      severity: 'Severity',
      detection: 'How it was found',
      failureSource: 'Recorded by',
    },
    filters: {
      criticality: 'Criticality',
      stopHours: 'Stop hours',
      interruption: 'Hours lost',
    },
  },
  fr: {
    assets: {
      label: 'Parc d\'équipements',
      help: 'Une ligne par équipement : sites, lignes, machines et composants',
    },
    stops: {
      label: 'Arrêts',
      help: 'Une ligne par arrêt machine, planifié ou non, avec ses heures',
    },
    failures: {
      label: 'Pannes',
      help: 'Une ligne par panne, avec sa cause et ce qu\'elle a interrompu. Pas de MTBF : il faudrait les heures de marche',
    },
    dates: {
      installed: 'Date d\'installation',
      created: 'Date de création',
      stopStarted: 'Début de l\'arrêt',
      stopEnded: 'Fin de l\'arrêt',
      failed: 'Date de la panne',
    },
    metrics: {
      assets: 'Équipements',
      active: 'En service',
      outOfService: 'Hors service',
      critical: 'Critiques',
      age: 'Âge moyen',
      downtime: 'Heures d\'arrêt',
      stops: 'Arrêts',
      stillStopped: 'Arrêts en cours',
      averageStop: 'Arrêt moyen',
      failures: 'Pannes',
      stopping: 'Pannes bloquantes',
      interruption: 'Heures perdues',
      mttr: 'MTTR',
      stoppingRate: 'Part des bloquantes',
    },
    dimensions: {
      level: 'Niveau',
      type: 'Type de machine',
      status: 'Statut',
      site: 'Site',
      criticality: 'Criticité',
      manufacturer: 'Fabricant',
      responsible: 'Responsable',
      stopAsset: 'Machine',
      stopKind: 'Planifié ou non',
      stopSource: 'Enregistré par',
      failureAsset: 'Machine',
      failureType: 'Type de panne',
      cause: 'Cause',
      severity: 'Gravité',
      detection: 'Mode de détection',
      failureSource: 'Enregistré par',
    },
    filters: {
      criticality: 'Criticité',
      stopHours: 'Heures d\'arrêt',
      interruption: 'Heures perdues',
    },
  },
}

/**
 * Adds the analytics vocabulary to the module's own catalogue, locale by locale.
 *
 * Merged here rather than pasted into the big literal so the block stays
 * readable and so adding a metric is one line in two files instead of four
 * edits in the middle of eight hundred.
 */
export function withAnalytics(base, namespace) {
  const merged = {}
  for (const [locale, catalogue] of Object.entries(base)) {
    merged[locale] = {
      ...catalogue,
      [namespace]: { ...catalogue[namespace], analytics: analytics[locale] },
    }
  }
  return merged
}

export default analytics
